// Bank-specific brand themes based on UAE bank design guidelines

export interface BankTheme {
  id: string
  name: string
  colors: {
    primary: string
    primaryDark: string
    primaryLight: string
    secondary: string
    accent: string
    text: string
    textLight: string
    background: string
    cardBg: string
  }
  gradients: {
    header: string
    button: string
    card: string
  }
  fonts: {
    heading: string
    body: string
  }
  borderRadius: string
  shadowStyle: string
}

export const BANK_THEMES: Record<string, BankTheme> = {
  emiratesnbd: {
    id: 'emiratesnbd',
    name: 'Emirates NBD',
    colors: {
      primary: '#003B5C',
      primaryDark: '#002A42',
      primaryLight: '#004D75',
      secondary: '#FFD100',
      accent: '#E6F0F5',
      text: '#1A1A1A',
      textLight: '#666666',
      background: '#F5F9FB',
      cardBg: '#FFFFFF',
    },
    gradients: {
      header: 'linear-gradient(135deg, #003B5C 0%, #002A42 100%)',
      button: 'linear-gradient(135deg, #003B5C 0%, #004D75 100%)',
      card: 'linear-gradient(135deg, #E6F0F5 0%, #F5F9FB 100%)',
    },
    fonts: {
      heading: 'system-ui, -apple-system, sans-serif',
      body: 'system-ui, -apple-system, sans-serif',
    },
    borderRadius: '14px',
    shadowStyle: '0 4px 12px rgba(0, 59, 92, 0.15)',
  },
  adcb: {
    id: 'adcb',
    name: 'Abu Dhabi Commercial Bank',
    colors: {
      primary: '#E31E24',
      primaryDark: '#C01A1F',
      primaryLight: '#E8383D',
      secondary: '#1A1A1A',
      accent: '#FFE8E9',
      text: '#1A1A1A',
      textLight: '#666666',
      background: '#FFF5F5',
      cardBg: '#FFFFFF',
    },
    gradients: {
      header: 'linear-gradient(135deg, #E31E24 0%, #C01A1F 100%)',
      button: 'linear-gradient(135deg, #E31E24 0%, #E8383D 100%)',
      card: 'linear-gradient(135deg, #FFE8E9 0%, #FFF5F5 100%)',
    },
    fonts: {
      heading: 'system-ui, -apple-system, sans-serif',
      body: 'system-ui, -apple-system, sans-serif',
    },
    borderRadius: '12px',
    shadowStyle: '0 4px 12px rgba(227, 30, 36, 0.15)',
  },
  adib: {
    id: 'adib',
    name: 'ADIB',
    colors: {
      primary: '#00A651',
      primaryDark: '#008C44',
      primaryLight: '#00C65E',
      secondary: '#C5A572',
      accent: '#E8F5E9',
      text: '#1A1A1A',
      textLight: '#666666',
      background: '#F5F9F6',
      cardBg: '#FFFFFF',
    },
    gradients: {
      header: 'linear-gradient(135deg, #00A651 0%, #008C44 100%)',
      button: 'linear-gradient(135deg, #00A651 0%, #00C65E 100%)',
      card: 'linear-gradient(135deg, #E8F5E9 0%, #F5F9F6 100%)',
    },
    fonts: {
      heading: 'system-ui, -apple-system, sans-serif',
      body: 'system-ui, -apple-system, sans-serif',
    },
    borderRadius: '16px',
    shadowStyle: '0 4px 16px rgba(0, 166, 81, 0.15)',
  },
  fab: {
    id: 'fab',
    name: 'First Abu Dhabi Bank',
    colors: {
      primary: '#5B2C6F',
      primaryDark: '#4A2359',
      primaryLight: '#7A3D8F',
      secondary: '#D4AF37',
      accent: '#F3E8F7',
      text: '#1A1A1A',
      textLight: '#666666',
      background: '#FAF7FB',
      cardBg: '#FFFFFF',
    },
    gradients: {
      header: 'linear-gradient(135deg, #5B2C6F 0%, #4A2359 100%)',
      button: 'linear-gradient(135deg, #5B2C6F 0%, #7A3D8F 100%)',
      card: 'linear-gradient(135deg, #F3E8F7 0%, #FAF7FB 100%)',
    },
    fonts: {
      heading: 'system-ui, -apple-system, sans-serif',
      body: 'system-ui, -apple-system, sans-serif',
    },
    borderRadius: '10px',
    shadowStyle: '0 2px 8px rgba(91, 44, 111, 0.15)',
  },
  mashreq: {
    id: 'mashreq',
    name: 'Mashreq Bank',
    colors: {
      primary: '#FF6600',
      primaryDark: '#E55A00',
      primaryLight: '#FF7F33',
      secondary: '#E31E24',
      accent: '#FFF3E6',
      text: '#1A1A1A',
      textLight: '#666666',
      background: '#FFF9F2',
      cardBg: '#FFFFFF',
    },
    gradients: {
      header: 'linear-gradient(135deg, #FF6600 0%, #E55A00 100%)',
      button: 'linear-gradient(135deg, #FF6600 0%, #FF7F33 100%)',
      card: 'linear-gradient(135deg, #FFF3E6 0%, #FFF9F2 100%)',
    },
    fonts: {
      heading: 'system-ui, -apple-system, sans-serif',
      body: 'system-ui, -apple-system, sans-serif',
    },
    borderRadius: '12px',
    shadowStyle: '0 4px 12px rgba(255, 102, 0, 0.15)',
  },
  hsbc: {
    id: 'hsbc',
    name: 'HSBC',
    colors: {
      primary: '#DB0011',
      primaryDark: '#B00010',
      primaryLight: '#E6001A',
      secondary: '#000000',
      accent: '#FFE6E8',
      text: '#1A1A1A',
      textLight: '#666666',
      background: '#FFF5F6',
      cardBg: '#FFFFFF',
    },
    gradients: {
      header: 'linear-gradient(135deg, #DB0011 0%, #B00010 100%)',
      button: 'linear-gradient(135deg, #DB0011 0%, #E6001A 100%)',
      card: 'linear-gradient(135deg, #FFE6E8 0%, #FFF5F6 100%)',
    },
    fonts: {
      heading: 'system-ui, -apple-system, sans-serif',
      body: 'system-ui, -apple-system, sans-serif',
    },
    borderRadius: '4px',
    shadowStyle: '0 2px 8px rgba(219, 0, 17, 0.12)',
  },
  cbd: {
    id: 'cbd',
    name: 'Commercial Bank of Dubai',
    colors: {
      primary: '#00703C',
      primaryDark: '#005A30',
      primaryLight: '#008C4A',
      secondary: '#D4AF37',
      accent: '#E6F2EE',
      text: '#1A1A1A',
      textLight: '#666666',
      background: '#F5FAF8',
      cardBg: '#FFFFFF',
    },
    gradients: {
      header: 'linear-gradient(135deg, #00703C 0%, #005A30 100%)',
      button: 'linear-gradient(135deg, #00703C 0%, #008C4A 100%)',
      card: 'linear-gradient(135deg, #E6F2EE 0%, #F5FAF8 100%)',
    },
    fonts: {
      heading: 'system-ui, -apple-system, sans-serif',
      body: 'system-ui, -apple-system, sans-serif',
    },
    borderRadius: '12px',
    shadowStyle: '0 4px 12px rgba(0, 112, 60, 0.15)',
  },
  dib: {
    id: 'dib',
    name: 'Dubai Islamic Bank',
    colors: {
      primary: '#00A859',
      primaryDark: '#008C4A',
      primaryLight: '#00C66E',
      secondary: '#C5A572',
      accent: '#E8F5E9',
      text: '#1A1A1A',
      textLight: '#666666',
      background: '#F5F9F6',
      cardBg: '#FFFFFF',
    },
    gradients: {
      header: 'linear-gradient(135deg, #00A859 0%, #008C4A 100%)',
      button: 'linear-gradient(135deg, #00A859 0%, #00C66E 100%)',
      card: 'linear-gradient(135deg, #E8F5E9 0%, #F5F9F6 100%)',
    },
    fonts: {
      heading: 'system-ui, -apple-system, sans-serif',
      body: 'system-ui, -apple-system, sans-serif',
    },
    borderRadius: '14px',
    shadowStyle: '0 4px 14px rgba(0, 168, 89, 0.15)',
  },
}

// Helper to get theme by bank ID
export const getBankTheme = (bankId: string): BankTheme | null => {
  return BANK_THEMES[bankId] || null
}

// Helper to get short bank name for mobile displays
export const getShortBankName = (bankName: string): string => {
  const shortNames: Record<string, string> = {
    'Abu Dhabi Commercial Bank': 'ADCB',
    'Emirates NBD': 'Emirates NBD',
    ADIB: 'ADIB',
    'First Abu Dhabi Bank': 'FAB',
    'Mashreq Bank': 'Mashreq',
    HSBC: 'HSBC',
    'Commercial Bank of Dubai': 'CBD',
    'Dubai Islamic Bank': 'DIB',
  }
  return shortNames[bankName] || bankName
}

// Default theme for unknown banks
export const DEFAULT_BANK_THEME: BankTheme = {
  id: 'default',
  name: 'Bank',
  colors: {
    primary: '#1e3a5f',
    primaryDark: '#152b47',
    primaryLight: '#2a4a75',
    secondary: '#4DB8C4',
    accent: '#E6F4F7',
    text: '#1A1A1A',
    textLight: '#666666',
    background: '#F5F9FA',
    cardBg: '#FFFFFF',
  },
  gradients: {
    header: 'linear-gradient(135deg, #1e3a5f 0%, #152b47 100%)',
    button: 'linear-gradient(135deg, #1e3a5f 0%, #2a4a75 100%)',
    card: 'linear-gradient(135deg, #E6F4F7 0%, #F5F9FA 100%)',
  },
  fonts: {
    heading: 'system-ui, -apple-system, sans-serif',
    body: 'system-ui, -apple-system, sans-serif',
  },
  borderRadius: '12px',
  shadowStyle: '0 4px 12px rgba(30, 58, 95, 0.15)',
}
